import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../utils/api';
import { filterActiveClientCycles } from '../utils/cycleStatuses';
import { buildIntegrationsUrl, buildProjectUrl } from '../utils/deepLink';
import { useActiveProject } from './ActiveProjectContext';

const RoleNavContext = createContext(null);

export function RoleNavProvider({ children }) {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { activeProjectId, projects: activeProjects } = useActiveProject();
  const [defaultProjectId, setDefaultProjectId] = useState(null);
  const [badges, setBadges] = useState({
    cycles: 0,
    bugs: 0,
    openCycles: 0,
    myCycles: 0,
    unreadMessages: 0,
  });
  const [activeCycleId, setActiveCycleId] = useState(null);

  const isClient = user?.role === 'client' || user?.role === 'client_team';
  const isTester = user?.role === 'tester';

  const fetchClientNav = useCallback(async () => {
    if (!isClient) return;
    try {
      const pid = activeProjectId || null;
      if (!pid) {
        setBadges((b) => ({ ...b, bugs: 0, cycles: 0 }));
        return;
      }
      const statsPath = `/dashboard/stats/${pid}`;
      const cycleParams = { projectId: pid };
      const [statsRes, cyclesRes] = await Promise.all([
        api.get(statsPath, { params: { defectRange: '7d', trendRange: '7d' } }),
        api.get('/test-cycles', { params: cycleParams }),
      ]);
      const preferredId =
        activeProjectId ||
        (activeProjects.length === 1 ? activeProjects[0].id : null) ||
        defaultProjectId;
      if (preferredId) {
        setDefaultProjectId(preferredId);
      }
      const stats = statsRes.data?.stats || {};
      const cycles = Array.isArray(cyclesRes.data)
        ? cyclesRes.data
        : cyclesRes.data?.testCycles || [];
      const active = filterActiveClientCycles(cycles);
      setBadges((b) => ({
        ...b,
        bugs: parseInt(stats.activeBugs?.value, 10) || stats.bugsOpenClosed?.open || 0,
        cycles: Math.max(active.length, stats.runningCycles || 0),
      }));
    } catch (e) {
      console.warn('RoleNavContext client', e);
    }
  }, [isClient, activeProjectId, activeProjects, defaultProjectId]);

  const fetchTesterNav = useCallback(async () => {
    if (!isTester) return;
    try {
      const [openRes, cyclesRes, unreadRes] = await Promise.all([
        api.get('/test-cycles/open', { params: { summary: '1' } }),
        api.get('/test-cycles'),
        api.get('/dm/unread-counts').catch(() => ({ data: { counts: {} } })),
      ]);
      const open = openRes.data?.cycles || [];
      const mine = Array.isArray(cyclesRes.data) ? cyclesRes.data : cyclesRes.data?.testCycles || [];
      const active = mine.filter((c) =>
        ['in-progress', 'planned', 'draft', 'active'].includes(c.status)
      );
      if (active.length > 0) {
        setActiveCycleId(active[0].id);
      }
      const counts = unreadRes.data?.counts || {};
      const unreadMessages = Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0);
      setBadges((b) => ({
        ...b,
        openCycles: open.length,
        myCycles: active.length,
        unreadMessages,
      }));
    } catch (e) {
      console.warn('RoleNavContext tester', e);
    }
  }, [isTester]);

  useEffect(() => {
    fetchClientNav();
  }, [fetchClientNav, user?.id]);

  // Kept separate from the client effect so client-side dependency churn can never
  // restart this poll. Skips ticks while the tab is hidden.
  useEffect(() => {
    if (!isTester) return undefined;
    fetchTesterNav();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchTesterNav();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchTesterNav, user?.id, isTester]);

  const navigateToProjectTab = useCallback(
    (tab, projectIdOverride) => {
      const pid = projectIdOverride || activeProjectId || defaultProjectId;
      if (!pid) {
        navigate('/app/projects');
        return;
      }
      if (tab === 'cycles') {
        navigate(buildProjectUrl(pid, 'cycles'));
        return;
      }
      if (tab === 'defects' || tab === 'issues' || tab === 'bugs') {
        navigate(buildProjectUrl(pid, 'defects'));
        return;
      }
      if (tab === 'integrations') {
        navigate(pid ? buildIntegrationsUrl({ projectId: pid }) : '/app/integrations');
        return;
      }
      if (tab === 'forms') {
        navigate(buildProjectUrl(pid, 'forms'));
        return;
      }
      if (tab === 'reports' || tab === 'test_cases' || tab === 'test-cases') {
        navigate(buildProjectUrl(pid, 'test_cases'));
        return;
      }
      navigate(buildProjectUrl(pid, tab || 'dashboard'));
    },
    [defaultProjectId, activeProjectId, navigate]
  );

  const getReportBugPath = useCallback(() => {
    if (activeCycleId) {
      return `/app/test-cycles/${activeCycleId}/create-issue`;
    }
    return '/app/dashboard';
  }, [activeCycleId]);

  const value = {
    defaultProjectId,
    badges,
    activeCycleId,
    navigateToProjectTab,
    getReportBugPath,
    refreshNav: () => {
      fetchClientNav();
      fetchTesterNav();
    },
  };

  return <RoleNavContext.Provider value={value}>{children}</RoleNavContext.Provider>;
}

export function useRoleNav() {
  const ctx = useContext(RoleNavContext);
  return ctx || {
    defaultProjectId: null,
    badges: { cycles: 0, bugs: 0, openCycles: 0, myCycles: 0, unreadMessages: 0 },
    activeCycleId: null,
    navigateToProjectTab: () => {},
    getReportBugPath: () => '/app/dashboard',
    refreshNav: () => {},
  };
}
